import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle, Building2, ShieldCheck } from 'lucide-react';

const ROLE_LABELS = {
  admin: 'Amministratore',
  sales_manager: 'Resp. Commerciale',
  sales_rep: 'Commerciale',
  operator: 'Operatore',
  client: 'Cliente',
};

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalidReason, setInvalidReason] = useState('');
  const [invalidMessage, setInvalidMessage] = useState('');

  const [form, setForm] = useState({ first_name: '', last_name: '', password: '', confirm_password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidReason('invalid');
      setInvalidMessage('Nessun token trovato. Usa il link ricevuto via email.');
      setValidating(false);
      return;
    }
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/invitations/validate?token=${encodeURIComponent(token)}`);
        if (res.data.valid) {
          setInvite(res.data);
        } else {
          setInvalidReason(res.data.reason);
          setInvalidMessage(res.data.message);
        }
      } catch {
        setInvalidReason('invalid');
        setInvalidMessage('Errore nella validazione del link. Riprova o contatta l\'amministratore.');
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('Nome e cognome sono obbligatori');
      return;
    }
    if (form.password.length < 6) {
      setError('La password deve avere almeno 6 caratteri');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Le password non coincidono');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/invitations/accept`, { token, ...form });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-primary" size={32} />
          <p className="text-muted-foreground text-sm">Validazione invito...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-8 pb-8 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Registrazione completata!</h2>
              <p className="text-muted-foreground text-sm mt-2">
                Il tuo account è stato creato. Puoi ora accedere con la tua email e password.
              </p>
            </div>
            <Button className="w-full mt-2" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              Vai al login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-8 pb-8 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <XCircle size={32} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Link non valido</h2>
              <p className="text-muted-foreground text-sm mt-2">{invalidMessage}</p>
              {invalidReason === 'expired' && (
                <p className="text-xs text-muted-foreground mt-2">Contatta il tuo amministratore e chiedi un nuovo invito.</p>
              )}
            </div>
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
              Torna al login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShieldCheck size={24} className="text-primary" />
            </div>
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Completa la registrazione
          </CardTitle>
          <CardDescription className="text-sm">
            Sei stato invitato ad accedere al CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Invite info */}
          <div className="bg-muted/40 rounded-lg p-3 mb-5 space-y-2 border border-border">
            <div className="flex items-center gap-2 text-sm">
              <Building2 size={13} className="text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">Azienda:</span>
              <span className="font-medium">{invite.tenant_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground ml-[21px]">Email:</span>
              <span className="font-medium">{invite.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground ml-[21px]">Ruolo assegnato:</span>
              <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
                {ROLE_LABELS[invite.role] || invite.role}
              </Badge>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome *</Label>
                <Input
                  value={form.first_name}
                  onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                  placeholder="Mario"
                  autoFocus
                  required
                  autoComplete="given-name"
                  data-testid="reg-first-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Cognome *</Label>
                <Input
                  value={form.last_name}
                  onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                  placeholder="Rossi"
                  required
                  autoComplete="family-name"
                  data-testid="reg-last-name"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Almeno 6 caratteri"
                  required
                  autoComplete="new-password"
                  data-testid="reg-password"
                />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Conferma password *</Label>
              <div className="relative">
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm_password}
                  onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                  required
                  autoComplete="new-password"
                  data-testid="reg-confirm-password"
                />
                <button type="button" onClick={() => setShowConfirm(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="reg-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Crea il mio account
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
